import React, { useMemo, useState, useEffect } from 'react';
import {
    ResponsiveContainer,
    BarChart, Bar,
    LineChart, Line,
    AreaChart, Area,
    PieChart, Pie, Cell,
    ScatterChart, Scatter,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { McIcon } from '@maersk-global/mds-react-wrapper';

type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'scatter';

interface ChartVisualizationProps {
    /** Array of records (e.g. testResults.data) */
    data: any[];
}

const COLORS = [
    '#0073ab', '#42b0d5', '#f59e0b', '#10b981', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

const isNumericValue = (v: any): boolean => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'string') {
        const n = Number(v);
        return !Number.isNaN(n) && Number.isFinite(n);
    }
    return false;
};

const toNumber = (v: any): number => {
    if (typeof v === 'number') return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export const ChartVisualization: React.FC<ChartVisualizationProps> = ({ data }) => {
    const columns = useMemo(() => {
        if (!data || data.length === 0) return [] as string[];
        return Object.keys(data[0]);
    }, [data]);

    // Detect numeric columns by sampling first ~50 rows
    const numericColumns = useMemo(() => {
        if (!data || data.length === 0) return [] as string[];
        const sample = data.slice(0, 50);
        return columns.filter(col => {
            const vals = sample.map(r => r?.[col]).filter(v => v !== null && v !== undefined && v !== '');
            if (vals.length === 0) return false;
            return vals.every(isNumericValue);
        });
    }, [data, columns]);

    const categoricalColumns = useMemo(
        () => columns.filter(c => !numericColumns.includes(c)),
        [columns, numericColumns],
    );

    const [chartType, setChartType] = useState<ChartType>('bar');
    const [xAxis, setXAxis] = useState<string>('');
    const [yAxes, setYAxes] = useState<string[]>([]);
    const hasNumericColumns = numericColumns.length > 0;

    const availableChartTypes = useMemo<ChartType[]>(
        () => hasNumericColumns ? ['bar', 'line', 'area', 'pie', 'scatter'] : ['bar', 'pie'],
        [hasNumericColumns],
    );

    // Initialize defaults when data shape changes
    useEffect(() => {
        if (columns.length === 0) return;
        const defaultX = categoricalColumns[0] || columns[0];
        const defaultY = numericColumns.length > 0 ? [numericColumns[0]] : [];
        setXAxis(prev => (prev && columns.includes(prev) ? prev : defaultX));
        setYAxes(prev => {
            const filtered = prev.filter(c => numericColumns.includes(c));
            return filtered.length ? filtered : defaultY;
        });
    }, [columns, categoricalColumns, numericColumns]);

    useEffect(() => {
        if (!availableChartTypes.includes(chartType)) {
            setChartType('bar');
        }
    }, [availableChartTypes, chartType]);

    const chartData = useMemo(() => {
        if (!data || !xAxis) return [];
        return data.map(row => {
            const out: Record<string, any> = { [xAxis]: row?.[xAxis] };
            yAxes.forEach(y => { out[y] = toNumber(row?.[y]); });
            return out;
        });
    }, [data, xAxis, yAxes]);

    const pieData = useMemo(() => {
        if (chartType !== 'pie' || !xAxis) return [];
        // Aggregate (sum) by xAxis
        const map = new Map<string, number>();
        data.forEach(row => {
            const key = String(row?.[xAxis] ?? '');
            if (hasNumericColumns && yAxes.length > 0) {
                const yKey = yAxes[0];
                map.set(key, (map.get(key) || 0) + toNumber(row?.[yKey]));
            } else {
                map.set(key, (map.get(key) || 0) + 1);
            }
        });
        return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
    }, [chartType, data, xAxis, yAxes, hasNumericColumns]);

    const frequencyData = useMemo(() => {
        if (!xAxis) return [];
        const map = new Map<string, number>();
        data.forEach(row => {
            const key = String(row?.[xAxis] ?? '');
            map.set(key, (map.get(key) || 0) + 1);
        });
        return Array.from(map.entries()).map(([name, count]) => ({ [xAxis]: name, __count: count }));
    }, [data, xAxis]);

    if (!data || data.length === 0) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px 16px', color: '#9ca3af', textAlign: 'center',
            }}>
                <McIcon icon="chart-column" style={{ fontSize: 32, marginBottom: 8 }} />
                <p className="mds-text--x-small-regular">Run the query to visualize results.</p>
            </div>
        );
    }

    const toggleY = (col: string) => {
        setYAxes(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
    };

    const ctrlStyle: React.CSSProperties = {
        padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db',
        borderRadius: 4, background: '#fff', color: '#111827',
    };
    const labelStyle: React.CSSProperties = { fontSize: 11, color: '#6b7280', fontWeight: 600 };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
            {/* Controls */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={labelStyle}>Chart Type</span>
                    <select
                        value={chartType}
                        onChange={e => setChartType(e.target.value as ChartType)}
                        style={ctrlStyle}
                    >
                        {availableChartTypes.map(type => (
                            <option key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={labelStyle}>{chartType === 'pie' ? 'Category' : 'X Axis'}</span>
                    <select
                        value={xAxis}
                        onChange={e => setXAxis(e.target.value)}
                        style={ctrlStyle}
                    >
                        {columns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 200 }}>
                    <span style={labelStyle}>
                        {hasNumericColumns
                            ? (chartType === 'pie' ? 'Value (first selected)' : 'Y Axis (numeric, multi-select)')
                            : 'Value'}
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {hasNumericColumns ? numericColumns.map((c, i) => {
                            const active = yAxes.includes(c);
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => toggleY(c)}
                                    style={{
                                        ...ctrlStyle,
                                        cursor: 'pointer',
                                        background: active ? COLORS[i % COLORS.length] : '#fff',
                                        color: active ? '#fff' : '#111827',
                                        borderColor: active ? COLORS[i % COLORS.length] : '#d1d5db',
                                    }}
                                >
                                    {c}
                                </button>
                            );
                        }) : (
                            <span style={{ ...ctrlStyle, display: 'inline-flex', alignItems: 'center' }}>
                                Auto count per {xAxis || 'key'}
                            </span>
                        )}
                    </div>
                </div>

                <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {data.length} row{data.length === 1 ? '' : 's'}
                </div>
            </div>

            {/* Chart canvas */}
            <div style={{ flex: 1, minHeight: 320, width: '100%', background: '#fff' }}>
                {hasNumericColumns && yAxes.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 80, fontSize: 12 }}>
                        Select at least one numeric column to plot.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === 'bar' ? (
                            <BarChart data={hasNumericColumns ? chartData : frequencyData} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {(hasNumericColumns ? yAxes : ['__count']).map((y, i) => (
                                    <Bar key={y} dataKey={y} fill={COLORS[i % COLORS.length]} />
                                ))}
                            </BarChart>
                        ) : chartType === 'line' ? (
                            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {yAxes.map((y, i) => (
                                    <Line key={y} type="monotone" dataKey={y} stroke={COLORS[i % COLORS.length]} dot={false} />
                                ))}
                            </LineChart>
                        ) : chartType === 'area' ? (
                            <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {yAxes.map((y, i) => (
                                    <Area key={y} type="monotone" dataKey={y} stroke={COLORS[i % COLORS.length]}
                                        fill={COLORS[i % COLORS.length]} fillOpacity={0.25} />
                                ))}
                            </AreaChart>
                        ) : chartType === 'scatter' ? (
                            <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} type="category" />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {yAxes.map((y, i) => (
                                    <Scatter key={y} name={y} data={chartData.map(d => ({ [xAxis]: d[xAxis], [y]: d[y] }))}
                                        fill={COLORS[i % COLORS.length]} dataKey={y} />
                                ))}
                            </ScatterChart>
                        ) : (
                            <PieChart>
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius="75%"
                                    label={(entry: any) => entry.name}
                                >
                                    {pieData.map((_, i) => (
                                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        )}
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default ChartVisualization;
